import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Star, Shield } from 'lucide-react';
import {
  TIERS,
  TIER_ORDER,
  FEATURE_ROWS,
  tierHasModule,
  formatLimit,
  type TierKey,
} from '../../configs/workspaceTiers';

const TRUST_POINTS = [
  {
    icon: <Star className="w-6 h-6 text-purple-600" />,
    title: 'Trusted by 500+ Cafes',
    description: 'Join a growing community of food entrepreneurs scaling their business.',
  },
  {
    icon: <Zap className="w-6 h-6 text-purple-600" />,
    title: 'Instant Workspace Setup',
    description: 'Get your POS ready in seconds. No complex migrations or downtime.',
  },
  {
    icon: <Shield className="w-6 h-6 text-purple-600" />,
    title: 'Free Plan Included',
    description: 'Start with our comprehensive free tier and upgrade whenever you are ready.',
  },
];

const LIMIT_ROWS: { key: keyof typeof TIERS.free.limits; label: string; suffix: string }[] = [
  { key: 'maxUsers', label: 'Staff accounts', suffix: 'staff' },
  { key: 'maxOrders', label: 'Orders / month', suffix: 'orders' },
  { key: 'maxMenuItems', label: 'Menu items', suffix: 'items' },
  { key: 'maxLocations', label: 'Locations', suffix: 'locations' },
  { key: 'smsQuota', label: 'SMS quota / period', suffix: 'SMS' },
  { key: 'dataRetentionMonths', label: 'Data retention', suffix: 'months' },
];

const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = React.useState<'monthly' | 'yearly'>('monthly');

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const getPrice = (tier: TierKey) => {
    const p = TIERS[tier].pricing;
    if (p.monthly < 0) return 'Custom';
    if (p.monthly === 0) return 'Free';
    return billingCycle === 'monthly' ? `₹${p.monthly}/mo` : `₹${p.yearly}/yr`;
  };

  const getStartedButton = (tier: TierKey, fullWidth = false) => {
    if (tier === 'free') {
      return (
        <Link
          to="/create-business"
          className={`inline-flex items-center justify-center px-6 py-2.5 border-2 border-purple-500 text-purple-600 dark:text-purple-400 font-bold rounded-full hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors ${fullWidth ? 'w-full' : ''}`}
        >
          Get started
        </Link>
      );
    }
    return (
      <button
        type="button"
        onClick={() => scrollTo('contact-section')}
        className={`inline-flex items-center justify-center px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-bold rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${fullWidth ? 'w-full' : ''}`}
      >
        Contact us
      </button>
    );
  };

  return (
    <div className="bg-gradient-to-b from-purple-50/50 via-white to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-16 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4 leading-tight">
          Boost revenue of your cafe business with a plan
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
          Compare limits and modules side by side. All plans run on the same CafeLawn workspace—pick
          the tier that fits your business today and upgrade when you&apos;re ready.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/create-business"
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-extrabold rounded-full text-lg shadow-lg shadow-purple-500/25 hover:opacity-90 transition-opacity"
          >
            Get started for free
          </Link>
          <button
            type="button"
            onClick={() => scrollTo('pricing-cards-section')}
            className="px-8 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 font-extrabold rounded-full text-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Choose a plan
          </button>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 sm:p-8 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl">
          {TRUST_POINTS.map((item) => (
            <div key={item.title} className="flex items-start gap-4 md:px-4 md:border-r md:last:border-r-0 border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h3 className="font-extrabold text-gray-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Billing toggle */}
      <div id="pricing-cards-section" className="flex justify-center items-center gap-4 mb-10 px-4">
        <div className="inline-flex items-center p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-full relative">
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-1.5 rounded-full text-sm font-extrabold transition-colors z-10 ${
              billingCycle === 'monthly' ? 'text-purple-600' : 'text-gray-500'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-1.5 rounded-full text-sm font-extrabold transition-colors z-10 ${
              billingCycle === 'yearly' ? 'text-purple-600' : 'text-gray-500'
            }`}
          >
            Yearly
          </button>
        </div>
        <span className="text-xs font-extrabold text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
          Save 20%
        </span>
      </div>

      {/* Tier cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {TIER_ORDER.map((tier) => (
            <div
              key={tier}
              className={`flex flex-col p-6 rounded-2xl border ${
                tier === 'free'
                  ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-900/10 ring-2 ring-purple-500/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              <h3 className="text-lg font-extrabold text-gray-900 dark:text-white">{TIERS[tier].name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{TIERS[tier].description}</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white mb-6">{getPrice(tier)}</p>
              <div className="mt-auto">{getStartedButton(tier, true)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features table */}
      <section id="features-section" className="max-w-5xl mx-auto px-4 sm:px-6 mb-20">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 text-center">Feature comparison</h2>
        <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Feature</th>
                {TIER_ORDER.map((tier) => (
                  <th key={tier} className="p-4 font-semibold text-gray-700 dark:text-gray-300 text-center">
                    {TIERS[tier].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row) => (
                <tr key={row.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.label}</td>
                  {TIER_ORDER.map((tier) => (
                    <td key={tier} className="p-4 text-center">
                      {tierHasModule(tier, row.id) ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Limits table */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-20">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 text-center">Plan limits</h2>
        <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Limit</th>
                {TIER_ORDER.map((tier) => (
                  <th key={tier} className="p-4 font-semibold text-gray-700 dark:text-gray-300 text-center">
                    {TIERS[tier].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LIMIT_ROWS.map((row) => (
                <tr key={row.key} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.label}</td>
                  {TIER_ORDER.map((tier) => (
                    <td key={tier} className="p-4 text-center text-gray-600 dark:text-gray-400">
                      {formatLimit(TIERS[tier].limits[row.key], row.suffix)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Contact */}
      <section id="contact-section" className="max-w-2xl mx-auto px-4 sm:px-6 pb-20 text-center">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-3">Need a custom plan?</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Contact our team for Growth, Business, or Enterprise pricing tailored to your operations.
        </p>
        <a
          href="mailto:support@cafelawn.com?subject=Plan%20Inquiry"
          className="inline-flex px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:opacity-90 transition-opacity"
        >
          Contact us
        </a>
      </section>
    </div>
  );
};

export default PricingPage;
